import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';
import { Url } from '@rossum/api-client';
import { Engine } from '@rossum/api-client/engines';
import { DeleteConfirmationDialog } from '@rossum/components/delete-confirmation-dialog/DeleteConfirmationDialog';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import { Button, Stack } from '@rossum/ui/material';
import { ConnectedQueueTile } from '../components/ConnectedQueueTile';
import { ConnectQueueDialog } from '../components/ConnectQueueDialog';
import { EngineErrorAlert } from '../components/EngineErrorAlert';
import { TilesListSkeleton } from '../components/skeletons/TilesListSkeleton';
import { TilesList } from '../components/TilesList';
import { TilesListEmptyState } from '../components/TilesListEmptyState';
import { EngineQueueSettingsHeader } from '../headers/EngineQueueSettingsHeader';
import { useEngine } from '../hooks/useEngine';
import { useInfiniteQueueStatsForEngine } from '../hooks/useInfiniteQueueStatsForEngine';
import { usePatchEngine } from '../hooks/usePatchEngine';
import { useUnpaginatedEngineFields } from '../hooks/useUnpaginatedEngineFields';
import { ENGINE_ID_KEY } from '../routes';

export const EngineQueueSettingsContent = ({ engine }: { engine: Engine }) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [queueToDisconnect, setQueueToDisconnect] = useState<Url | null>(null);

  const { data: engineFieldsData } = useUnpaginatedEngineFields(
    {
      engine: engine.id,
    },
    { enabled: true }
  );

  const {
    data: queueStats,
    fetchNextPage,
    hasNextPage,
    isFetching,
    status: queuesInfoStatus,
  } = useInfiniteQueueStatsForEngine(engine.id);

  const { mutate: patchEngine, isLoading: isPatchingEngine } = usePatchEngine(
    engine.id
  );

  useEffect(() => {
    const interval = setInterval(() => {
      if (hasNextPage && !isFetching) {
        fetchNextPage();
      }
    }, 500);

    // Stop the interval when there are no more pages
    if (!hasNextPage) {
      clearInterval(interval);
    }

    // Cleanup on unmount
    return () => clearInterval(interval);
  }, [hasNextPage, fetchNextPage, isFetching]);

  return (
    <PageLayout
      renderHeader={params => (
        <EngineQueueSettingsHeader
          {...params}
          engine={engine}
          isSaving={false}
        />
      )}
    >
      <ConnectQueueDialog
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={queueUrls =>
          patchEngine({
            trainingQueues: engine.trainingQueues.concat(queueUrls),
          })
        }
        isLoading={isPatchingEngine}
        trainingQueueUrls={engine.trainingQueues}
      />
      <DeleteConfirmationDialog
        title={intl.formatMessage({
          id: 'features.engines.engineDetail.connectedQueues.disconnectDialog.title',
        })}
        description={intl.formatMessage({
          id: 'features.engines.engineDetail.connectedQueues.disconnectDialog.description',
        })}
        open={!!queueToDisconnect}
        onCancel={() => setQueueToDisconnect(null)}
        onConfirm={() => {
          patchEngine(
            {
              trainingQueues: engine.trainingQueues.filter(
                queue => queue !== queueToDisconnect
              ),
            },
            { onSuccess: () => setQueueToDisconnect(null) }
          );
        }}
        isLoading={isPatchingEngine}
      />
      <Stack sx={{ p: 4 }} spacing={2} useFlexGap>
        <TilesList
          title={intl.formatMessage({
            id: 'features.engines.engineDetail.connectedQueues.title',
          })}
          items={queueStats.results.filter(
            ({ stats }) => !!stats?.predictionQueue
          )}
          renderTile={({ queue, workspace, stats }) => (
            <ConnectedQueueTile
              dataCy="connected-queue"
              key={queue.id}
              queue={queue}
              workspace={workspace}
              connectedFieldsCount={stats?.numberOfUsedEngineFields}
              totalFieldsCount={engineFieldsData?.length}
            />
          )}
          status={queuesInfoStatus}
          emptyState={
            <TilesListEmptyState
              title={intl.formatMessage({
                id: 'features.engines.engineDetail.connectedQueues.empty.title',
              })}
            />
          }
        />
        <TilesList
          title={intl.formatMessage({
            id: 'features.engines.engineDetail.trainingQueues.title',
          })}
          items={queueStats.results.filter(
            ({ stats }) => !!stats?.trainingQueue
          )}
          renderTile={({ queue, workspace, stats }) => (
            <ConnectedQueueTile
              dataCy="training-queue"
              key={queue.id}
              queue={queue}
              workspace={workspace}
              connectedFieldsCount={stats?.numberOfUsedEngineFields}
              totalFieldsCount={engineFieldsData?.length}
              onRemove={url => setQueueToDisconnect(url)}
            />
          )}
          status={queuesInfoStatus}
          emptyState={
            <Stack>
              <TilesListEmptyState
                title={intl.formatMessage({
                  id: 'features.engines.engineDetail.trainingQueues.empty.title',
                })}
              />
              <Button
                sx={{ alignSelf: 'center' }}
                onClick={() => setOpen(true)}
                variant="contained"
              >
                {intl.formatMessage({
                  id: 'features.engines.engineDetail.connectedQueues.connectButton',
                })}
              </Button>
            </Stack>
          }
          buttons={[
            <Button
              key="connect-queue"
              onClick={() => setOpen(true)}
              variant="contained"
            >
              {intl.formatMessage({
                id: 'features.engines.engineDetail.connectedQueues.connectButton',
              })}
            </Button>,
          ]}
        />
      </Stack>
    </PageLayout>
  );
};

export const EngineQueueSettingsPage = () => {
  const params = useParams<{ [ENGINE_ID_KEY]: string }>();
  const engineIdNumber = parseInt(params[ENGINE_ID_KEY], 10);
  const {
    data: engine,
    isLoading: engineIsLoading,
    isError,
  } = useEngine(engineIdNumber);

  if (isError) {
    return (
      <PageLayout>
        <EngineErrorAlert />
      </PageLayout>
    );
  }

  if (engineIsLoading) {
    return (
      <PageLayout>
        <TilesListSkeleton />
      </PageLayout>
    );
  }

  if (!engine) {
    return null;
  }

  return <EngineQueueSettingsContent engine={engine} />;
};
